/**
 * EraScript — renders a chain step's text in the writing medium of its era.
 *
 *   Stone tablet → papyrus → vellum → parchment → printed page → newsprint
 *   → typewritten → mimeograph → CRT terminal → web.
 *
 * METHODOLOGY HOLDS in this component:
 *   #5 — The platform leaves every user with a clearer head than they came
 *        in with. The medium is the lesson: information's physical form is
 *        why the Telephone Game distorts. Stone survives unchanged. Papyrus
 *        decays and gets recopied. Computers cache and re-interpret. The
 *        user reads it AND sees it.
 *   ADDED — "If any era treatment makes the text harder to read, simplify
 *        the treatment, not the readability." Contrast on the rendered text
 *        is checked at design-time and never compromised; sound is opt-in
 *        via existing HUD soundOn; reduced-motion bypasses the type-in.
 *
 * Performance: pure CSS textures + Web Audio synth. No raster, no network.
 * Accessibility: actual content stays in semantic HTML; treatments degrade.
 * Reduced-motion: type-in animation is replaced with instant render.
 */
import { useEffect, useRef, useState } from 'react';
import { getEraMedium } from '../lib/eraMedia';

// One-shot type-in: chars-per-tick controls reveal speed; defaults tuned for
// readable pacing on each medium (faster on screens, slower on stone).
const PACING = {
  era0: 30, era1: 24, era2: 18, era3: 18, era4: 14, era5: 12,
  era6: 10, era7: 10, era8: 6,  era9: 4,
};

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const handler = (e) => setReduced(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else if (mq.addListener) mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, []);
  return reduced;
}

/* SVG-noise data URLs — used as repeating CSS backgrounds for grain.
 * Encoded once; cheaper than image network round-trip. Each grain texture
 * is a tiny tiling SVG that's interpolated by the rasterizer. */
const GRAIN_URLS = {
  // Stone — coarse grain, strong contrast
  stone: "url(\"data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0.18 0 0 0 0 0.13 0 0 0 0 0.07 0 0 0 0.42 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
  // Cave — soft mottled organic tone
  cave: "url(\"data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='220' height='220' viewBox='0 0 220 220'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.42' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0.10 0 0 0 0 0.06 0 0 0 0 0.03 0 0 0 0.55 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
  // Papyrus — fibrous, woven
  papyrus: "url(\"data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='turbulence' baseFrequency='0.7 0.05' numOctaves='1' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0.50 0 0 0 0 0.38 0 0 0 0 0.20 0 0 0 0.18 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
  // Vellum — fine animal-skin grain
  vellum: "url(\"data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='240' viewBox='0 0 240 240'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.6' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0.45 0 0 0 0 0.32 0 0 0 0 0.18 0 0 0 0.20 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
  // Paper — soft fiber texture
  paper: "url(\"data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='220' height='220' viewBox='0 0 220 220'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.05' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0.30 0 0 0 0 0.22 0 0 0 0 0.12 0 0 0 0.10 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
  // Newsprint — rough fibrous yellowed grain
  newsprint: "url(\"data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.8' numOctaves='1' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0.36 0 0 0 0 0.26 0 0 0 0 0.13 0 0 0 0.18 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
  // CRT — subtle pixel-noise + scanline emphasis
  crt: "url(\"data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180' viewBox='0 0 180 180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='2.5' numOctaves='1' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0 0 0 0 0 0.40 0 0 0 0 0 0 0 0 0.22 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
  // Default / web — no grain
  none: 'none',
};

function grainBackground(grainKey) {
  return GRAIN_URLS[grainKey] || GRAIN_URLS.none;
}

/**
 * Edge accents — chiseled corner notches, deckled edges, scanlines, etc.
 * Implemented as inset box-shadows + ::before pseudo masks via inline style.
 * Returns a partial style object to merge into the surface div.
 */
function edgeStyle(edge) {
  switch (edge) {
    case 'chiseled':
      return {
        boxShadow: 'inset 0 0 0 1px rgba(60,40,20,0.5), inset 6px 6px 18px rgba(0,0,0,0.18), inset -6px -6px 18px rgba(255,235,200,0.06)',
        clipPath: 'polygon(2% 0, 98% 1%, 100% 5%, 99% 95%, 97% 100%, 3% 99%, 1% 95%, 0% 5%)',
      };
    case 'rough-stone':
      return {
        boxShadow: 'inset 0 0 0 1px rgba(232,181,133,0.18), inset 0 0 30px rgba(0,0,0,0.4)',
      };
    case 'curled-scroll':
      return {
        boxShadow: 'inset 0 12px 14px -8px rgba(120,90,40,0.35), inset 0 -12px 14px -8px rgba(120,90,40,0.35), 0 6px 12px rgba(0,0,0,0.25)',
        borderRadius: '4px',
      };
    case 'deckled':
      return {
        boxShadow: '0 0 0 1px rgba(180,140,90,0.25), inset 0 0 28px rgba(140,100,60,0.18)',
      };
    case 'aged':
      return {
        boxShadow: 'inset 0 0 24px rgba(120,90,50,0.12), 0 2px 6px rgba(0,0,0,0.15)',
      };
    case 'columned':
      return {
        boxShadow: 'inset 0 0 0 1px rgba(80,60,30,0.18)',
      };
    case 'platen-roll':
      return {
        boxShadow: 'inset 0 6px 8px -6px rgba(60,40,20,0.4), inset 0 -6px 8px -6px rgba(60,40,20,0.4)',
      };
    case 'memo':
      return {
        boxShadow: 'inset 0 0 0 1px rgba(20,40,80,0.12), 0 1px 4px rgba(0,0,0,0.12)',
      };
    case 'scanlines':
      return {
        boxShadow: 'inset 0 0 22px rgba(0,255,100,0.08), 0 0 1px rgba(0,255,100,0.45)',
      };
    case 'clean':
    default:
      return {};
  }
}

/**
 * Drop cap — illuminated capital letter for medieval/early-modern eras.
 * Pure CSS via initial-letter where supported; CSS float fallback otherwise.
 */
function DropCap({ letter, color, accent }) {
  return (
    <span
      aria-hidden="true"
      style={{
        float: 'left',
        fontSize: '3.1em',
        lineHeight: '0.85',
        marginRight: '0.08em',
        marginTop: '0.08em',
        color,
        textShadow: `0 0 8px ${accent}55`,
        fontFamily: "'Uncial Antiqua', 'Pirata One', serif",
        fontWeight: 700,
      }}
    >
      {letter}
    </span>
  );
}

export default function EraScript({
  eraId,
  title,
  body,
  meta,                 // optional source/year/location metadata block
  animate = true,
  soundEnabled = false, // wire from HUD soundOn upstream
  className = '',
}) {
  const medium = getEraMedium(eraId);
  const reducedMotion = useReducedMotion();
  const shouldAnimate = animate && !reducedMotion;
  const [revealed, setRevealed] = useState(shouldAnimate ? 0 : (body || '').length);
  const intervalRef = useRef(null);

  // Re-run reveal on body change
  useEffect(() => {
    setRevealed(shouldAnimate ? 0 : (body || '').length);
  }, [body, shouldAnimate]);

  useEffect(() => {
    if (!shouldAnimate || !body) return undefined;
    const total = body.length;
    const charsPerTick = PACING[eraId] != null ? Math.max(1, Math.floor(80 / PACING[eraId])) : 4;
    let cursor = 0;
    let lineCharCounter = 0;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      cursor = Math.min(total, cursor + charsPerTick);
      lineCharCounter += charsPerTick;
      setRevealed(cursor);
      // Sound — fire one tap per chunk. The SFX library has its own enabled
      // gate (driven by HUD soundOn → setSfxEnabled), so we always invoke
      // and let the global soundOn be the single source of truth.
      try {
        const sfx = await import('../lib/sfx');
        const isLineEnd = medium.tool.dingEvery
          ? lineCharCounter >= medium.tool.dingEvery
          : false;
        if (isLineEnd) lineCharCounter = 0;
        sfx.playEraWriteTap(medium.tool, { charIndex: cursor, isLineEnd });
      } catch {
        // sfx import optional — silent fail
      }
      if (cursor < total) {
        // Pacing in ms per tick — tighter on screens, looser on stone
        const ms = 60 + Math.max(0, 60 - PACING[eraId] * 2);
        intervalRef.current = setTimeout(tick, ms);
      }
    };
    intervalRef.current = setTimeout(tick, 200);
    return () => {
      cancelled = true;
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
  }, [body, shouldAnimate, eraId, medium.tool]);

  const visible = body ? body.slice(0, revealed) : '';
  const hidden = body ? body.slice(revealed) : '';

  // Compose surface style
  const surfaceStyle = {
    background: medium.background,
    backgroundImage: [grainBackground(medium.grain), medium.backgroundOverlay].filter((v) => v && v !== 'none').join(', ') || 'none',
    color: medium.ink,
    fontFamily: medium.fontStack,
    fontWeight: medium.fontWeight,
    fontSize: medium.fontSize,
    letterSpacing: medium.letterSpacing,
    textShadow: medium.inkShadow,
    padding: '20px 22px',
    borderRadius: '4px',
    position: 'relative',
    transition: 'background-color 600ms ease',
    ...edgeStyle(medium.edgeStyle),
  };

  // Dropcap candidate — only for vellum/parchment eras + when body is long enough
  let bodyContent;
  if (medium.dropCap && body && body.length > 0) {
    const firstChar = visible.charAt(0) || body.charAt(0);
    const restVisible = visible.slice(1);
    bodyContent = (
      <>
        {revealed > 0 && <DropCap letter={firstChar} color={medium.dropCap.color} accent={medium.dropCap.accent} />}
        <span>{restVisible}</span>
        <span aria-hidden="true" style={{ opacity: 0.0 }}>{hidden}</span>
      </>
    );
  } else {
    bodyContent = (
      <>
        <span>{visible}</span>
        {/* invisible reservation so layout doesn't reflow as text appears */}
        <span aria-hidden="true" style={{ opacity: 0.0 }}>{hidden}</span>
        {shouldAnimate && revealed < (body || '').length && (
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block',
              width: '0.6em',
              borderBottom: `2px solid ${medium.ink}`,
              animation: 'tsogn-cursor-blink 1s steps(2) infinite',
              marginLeft: '1px',
              verticalAlign: 'baseline',
            }}
          />
        )}
      </>
    );
  }

  return (
    <div
      className={`era-script ${className}`}
      data-era-medium={medium.surface}
      data-testid={`era-script-${eraId}`}
      style={surfaceStyle}
    >
      {/* Medium label — small, top-right; teaches users what they're looking at */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 6,
          right: 10,
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: '8px',
          letterSpacing: '0.18em',
          opacity: 0.45,
          color: medium.ink,
          textShadow: 'none',
          pointerEvents: 'none',
        }}
        data-testid={`era-medium-label-${eraId}`}
      >
        {medium.label}
      </div>

      {title && (
        <h3
          style={{
            margin: '0 0 14px 0',
            fontFamily: medium.fontStack,
            fontWeight: 600,
            fontSize: '1.45rem',
            letterSpacing: medium.letterSpacing,
            color: medium.ink,
            textShadow: medium.inkShadow,
          }}
        >
          {title}
        </h3>
      )}

      <div
        style={{
          lineHeight: '1.65',
          minHeight: '1.4em',  // prevents collapse during type-in
          marginBottom: meta ? 14 : 0,
        }}
      >
        {bodyContent}
      </div>

      {meta && (
        <div
          style={{
            marginTop: 14,
            paddingTop: 8,
            borderTop: `1px dashed ${medium.ink}33`,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: '10px',
            letterSpacing: '0.06em',
            color: medium.ink,
            opacity: 0.7,
          }}
          data-testid={`era-script-meta-${eraId}`}
        >
          {meta}
        </div>
      )}
    </div>
  );
}
